import { EmptyState } from '@/components/EmptyState';
import { parseDisputeDecisions } from '@shared/factory-dispute';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlarmClock, AlarmClockOff, Archive, ArchiveRestore, ArrowDownToLine, ArrowRight, ArrowUpFromLine, Bot, CircleCheck, CircleSlash, FileIcon, GitMerge, Hourglass, KeyRound, Link2, ListTree, MessageSquare, MoreHorizontal, Pencil, Plus, Repeat, Shapes, SlidersHorizontal, Sparkles, Tag, Trash2, Unlink, UserMinus, UserPlus, type LucideIcon } from 'lucide-react';
import { isTaskPriority, TASK_PRIORITY_LABELS, type TaskAttachment, type TaskDetail, type TaskEvent, type TaskLabel, type TaskMember } from '@shared/types/tasks';
import type { RunSummary } from '@shared/types/factory';
import { StatusBadge } from '@/components/StatusBadge';
import { isRunActive, RUN_STATUS_META, runNumberFromActorLabel, runRef } from '@/components/factory/run-status';
import { runPath } from '@/navigation/project-paths';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { confirmDelete } from '@/components/confirm-lifecycle';
import { deleteTaskChat, editTaskChat } from '@/lib/tasks-api';
import { DescriptionBefore } from './DescriptionBefore';
import { dismissEvent, optimisticUpdate, usePendingEvent } from '@/stores/taskOptimistic';
import { cn } from '@/lib/utils';
import { formatBytes, formatDateTime } from '@/lib/formatters';
import { snoozeDay } from '@/lib/snooze';
import { describeTaskRepeat, type TaskRepeat } from '@shared/task-repeat';
import { useFormatTaskRef } from '@/lib/current-project';
import { useAuthStore } from '@/stores/authStore';
import { usePref } from '@/hooks/use-pref';
import { useFollowScroll } from '@/hooks/use-follow-scroll';
import { JumpToLatestButton } from '@/components/JumpToLatestButton';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { UI_PREFS } from '@/lib/ui-prefs';
import { MentionText } from './MentionText';
import { LinksOpenInDialogs } from '@/pages/link-dialog/link-scope';
import { hasSteerOptions, heldRunNumber, steerComment, steeredRunNumber, steersSentAfter } from './task-steer';
import { SteeredTag } from './SteeredTag';
import { prLabel } from './task-types';
import { MentionTextarea } from './MentionTextarea';
import { commentTooLong } from './comment-length';
import { displayToMarkup, markupToDisplay, type PickedMention } from './mention-text';
import { formatRelative, memberHandle, memberName } from './task-ui';
import { MemberAvatar } from '@/components/team/MemberAvatar';
import { TaskChatComposer } from './TaskChatComposer';
import { isPlayableVideo, sendMessage } from './task-files';
import { decisionRounds, showTaskDecisions } from './task-decisions';
import { deferredMarker } from '@shared/upstream-deferral';
import { showTaskSuggestions } from './task-suggestions';
import { showProposedSubtasks } from './task-subtask-proposals';
import { useAttachmentLink } from './task-attachment-link';
import { Spinner } from '@/components/ui/spinner';
import { canSendComment, isSyncedIn, upstreamNote, upstreamOpens } from './task-upstream';
import { SendCommentUpstreamDialog, type UpstreamTagging } from './SendUpstreamButton';
import { TASK_SOURCE_LABELS } from '@shared/types/task-sources';
import { isLongComment, splitCommentParts } from '@shared/comment-short';
import { useIsMobile } from '@/hooks/use-mobile';
import { collapsedStart, RECENT_COMMENTS, RECENT_COMMENTS_PHONE } from './timeline-collapse';

/**
 * SF-T-201: "Send to <source>" in a comment's ⋯ menu, on a task mirrored from an enabled source, for someone
 * who can edit it. `onSend` resolves true when it went.
 */
export interface CommentSend {
  sourceLabel: string;
  tagging?: UpstreamTagging;
  onSend: (eventId: string, mentionId: string | null) => Promise<boolean>;
}

/** Who or what a Waiting on line names: the name saved with it, else the member. */
function waitName(p: Record<string, any>, person: (id: unknown) => string): string {
  if (p.name) return String(p.name);
  if (p.waiting?.target === 'user') return person(p.waiting.user_id);
  if (p.waiting?.target === 'upstream') return 'upstream';
  return p.waiting?.target === 'ai' ? 'the AI builder' : 'another task';
}

const FIELD_NAMES: Record<string, string> = { size: 'size', kind: 'kind', priority: 'priority', prs: 'pull requests' };

function fieldLine(field: string, from: unknown, to: unknown): string {
  const name = FIELD_NAMES[field] ?? field;
  if (field === 'prs') {
    const before = Array.isArray(from) ? from.length : 0;
    const after = Array.isArray(to) ? to.length : 0;
    if (after > before) return after - before === 1 ? 'added a pull request' : `added ${after - before} pull requests`;
    if (after < before) return before - after === 1 ? 'removed a pull request' : `removed ${before - after} pull requests`;
    return 'updated a pull request’s status';
  }
  // SF-T-174: the machine chosen for the AI builder's runs, by its name then.
  if (field === 'ai_machine') {
    if (!to) return from ? `let the AI builder choose its machine (it was ${String(from)})` : 'let the AI builder choose its machine';
    return from ? `moved the AI builder's runs from ${String(from)} to ${String(to)}` : `chose ${String(to)} for the AI builder's runs`;
  }
  if (to === null || to === undefined) return `cleared the ${name}`;
  // SF-T-151: a priority by its name (High, not high).
  const shown = (value: unknown) => (field === 'priority' && isTaskPriority(value) ? TASK_PRIORITY_LABELS[value] : String(value));
  return from ? `changed the ${name} from ${shown(from)} to ${shown(to)}` : `set the ${name} to ${shown(to)}`;
}

/** Lines no one did: a snooze ending, a Repeat coming round, a task waited on finishing. */
function hasNoActor(event: TaskEvent): boolean {
  const p = event.payload;
  if (event.kind === 'unsnoozed') return p.reason === 'time';
  if (event.kind === 'repeated' || p.repeated_from) return true;
  if (event.kind === 'waiting_removed') return p.reason === 'done' || p.reason === 'finished' || p.reason === 'upstream_replied' || p.reason === 'unmirrored';
  return false;
}

function systemLine(event: TaskEvent, members: TaskMember[], labels: TaskLabel[], formatRef: (number: number) => string): string {
  const p = event.payload as Record<string, any>;
  const person = (id: unknown) => memberHandle(members.find((m) => m.user_id === id));
  switch (event.kind) {
    case 'created':
      // Made by a Repeat: no one did it.
      if (p.repeated_from) return `Made by the Repeat on ${formatRef(p.repeated_from.number)}${p.column_name ? `, in ${p.column_name}` : ''}`;
      return `created this task${p.column_name ? ` in ${p.column_name}` : ''}`;
    case 'moved':
      return `moved this from ${p.from ?? 'another column'} to ${p.to}`;
    case 'renamed':
      return `renamed this from “${p.from}”`;
    case 'described':
      return 'updated the description';
    case 'spec_changed':
      return 'updated the spec';
    case 'assigned':
      return event.actor_user_id === p.user_id ? 'joined this task' : `assigned ${person(p.user_id)}`;
    case 'unassigned':
      return event.actor_user_id === p.user_id ? 'left this task' : `removed ${person(p.user_id)}`;
    case 'context_added':
      return `linked ${p.context?.title ?? 'something'}`;
    case 'context_removed':
      return `unlinked ${p.context?.title ?? 'something'}`;
    case 'label_added':
      return `added the label ${labels.find((l) => l.id === p.label_id)?.name ?? p.name}`;
    case 'label_removed':
      return `removed the label ${labels.find((l) => l.id === p.label_id)?.name ?? p.name}`;
    case 'archived':
      return 'archived this task';
    case 'unarchived':
      return 'put this task back on the Board';
    case 'pr_batch_merged':
      // SF-T-198: merged by a Merge PRs batch from the Board.
      return `merged ${prLabel(String(p.url ?? ''))} with Merge PRs on the Board (${Number(p.position)} of ${Number(p.total)})`;
    case 'promoted':
      // T-27: a person confirmed the AI builder's draft, so it can be built.
      return p.via === 'ai_builder'
        ? 'promoted this draft by asking the AI builder to build it'
        : p.via === 'run'
          ? 'promoted this draft by running it'
          : 'promoted this draft from the AI builder';
    case 'snoozed':
      return typeof p.until === 'string' ? `snoozed this until ${snoozeDay(p.until)}` : 'snoozed this';
    case 'unsnoozed':
      // `time`: no one did it, the snooze ended (see SystemLine).
      return p.reason === 'time' ? 'Snooze ended: back on the Board' : p.reason === 'moved' ? 'ended the snooze by moving it' : 'unsnoozed this';
    case 'repeat_set': {
      const text = p.repeat ? describeTaskRepeat(p.repeat as TaskRepeat) : null;
      if (!text) return 'stopped this repeating';
      return `set this to repeat ${text.charAt(0).toLowerCase()}${text.slice(1)}${p.repeat.new_task ? ', as a new task each time' : ''}`;
    }
    case 'repeated':
      // No one did it: the Repeat came round (see SystemLine).
      if (p.new_task) return `Came round again as ${formatRef(p.new_task.number)}, which carries the Repeat on`;
      return p.reopened ? `Came round again: back in ${p.to ?? 'the first column'}` : 'Came round again (still open)';
    // Type, fields and Waiting on.
    case 'type_set':
      // Jev's pick (T-17): its line reads as a suggestion, kept until someone confirms or changes it.
      if (p.suggested) return `suggested the type ${p.to_name ?? 'for this task'}`;
      return `changed the type${p.from_name ? ` from ${p.from_name}` : ''} to ${p.to_name ?? 'another type'}`;
    case 'field_set':
      if (p.suggested) return `suggested the ${FIELD_NAMES[p.field] ?? p.field} ${String(p.to)}${p.from ? ` instead of ${String(p.from)}` : ''}`;
      return fieldLine(p.field, p.from, p.to);
    case 'waiting_added':
      return `set this waiting on ${waitName(p, person)}`;
    case 'waiting_removed':
      if (p.reason === 'replied') return `replied, so it’s no longer waiting on ${p.waiting?.target === 'user' && p.waiting.user_id === event.actor_user_id ? 'them' : waitName(p, person)}`;
      if (p.reason === 'done') return `${waitName(p, person)} is done, so it’s no longer waiting on it`;
      if (p.reason === 'finished') return `No longer waiting on ${waitName(p, person)}: this task is done`;
      if (p.reason === 'settled') return `settled the disputed review, so it’s no longer waiting on ${waitName(p, person)}`;
      if (p.reason === 'review') return `moved this to Review, so it’s no longer waiting on ${waitName(p, person)}`;
      // T-58: the task's source.
      if (p.reason === 'upstream_replied') return `${waitName(p, person)} replied, so it’s no longer waiting on it`;
      if (p.reason === 'answered') return `answered here, so it’s no longer waiting on ${waitName(p, person)}`;
      if (p.reason === 'unmirrored') return `No longer waiting on ${waitName(p, person)}: the task isn’t mirrored from it now`;
      return `stopped waiting on ${waitName(p, person)}`;
    case 'orphaned': {
      // T-71: by the source (its label is the actor), so "Trello took this off …".
      const parent = p.parent?.number ? formatRef(p.parent.number) : 'its parent';
      return p.back ? `put this back under ${parent}` : `took this off ${parent}; it stays under it here, marked orphaned`;
    }
    case 'subtask_promoted':
      // SF-T-147: on the task, its old parent; on the parent, the sub-task that left.
      if (p.subtask) return `made ${p.subtask.number ? formatRef(p.subtask.number) : 'a sub-task'} a task of its own`;
      return `made this a task of its own, off ${p.parent?.number ? formatRef(p.parent.number) : 'its parent'}`;
    case 'made_subtask':
      if (p.subtask) return `made ${p.subtask.number ? formatRef(p.subtask.number) : 'a task'} a sub-task of this`;
      return `made this a sub-task of ${p.parent?.number ? formatRef(p.parent.number) : 'another task'}`;
    case 'attachment_deleted':
      return `deleted the file ${typeof p.filename === 'string' ? p.filename : ''}`.trim();
    case 'merged': {
      // SF-T-103: merged on the Board, on the parent (its new sub-tasks) or on each sub-task (its parent).
      if (p.parent?.number) return `merged this into ${formatRef(p.parent.number)} as a sub-task`;
      const refs = (p.subtasks ?? []).map((t: { number: number }) => formatRef(t.number));
      const list = refs.length > 3 ? `${refs.slice(0, 3).join(', ')} and ${refs.length - 3} more` : refs.join(', ');
      return `${p.created ? 'made this by merging' : 'merged'} ${list || 'tasks'}${p.created ? ', as its sub-tasks' : ' into this as sub-tasks'}`;
    }
    case 'pushed_upstream':
      // SF-T-207: made here, then put on its source.
      return `pushed this to ${p.source_name ?? 'its source'}${p.ref ? ` as ${p.ref}` : ''}; it’s mirrored from there now`;
    case 'runs_closed': {
      // SF-T-279: a run's whole thread, closed: its questions close unanswered, and it isn't carried on.
      const runs: string[] = Array.isArray(p.runs) ? p.runs.map((n: number) => `R-${n}`) : [];
      const list = runs.length > 1 ? `${runs.slice(0, -1).join(', ')} and ${runs[runs.length - 1]}` : (runs[0] ?? 'a run');
      return `closed ${list}: ${runs.length > 1 ? 'their' : 'its'} questions are closed unanswered, and ${runs.length > 1 ? 'they aren’t' : 'it isn’t'} carried on`;
    }
    default:
      return 'updated this task';
  }
}

/** `opens`: a thumbnail's file upstream (T-99), which the link opens instead of the stored file. */
function FileLink({ a, opens }: { a: TaskAttachment; opens?: string }) {
  const link = useAttachmentLink(a);
  return (
    <a
      {...(opens ? { href: opens, target: '_blank', rel: 'noreferrer' } : link)}
      className="flex w-fit max-w-full items-center gap-2 rounded-md border px-2 py-1.5 text-xs hover:bg-muted"
    >
      <FileIcon className="size-4 shrink-0 text-muted-foreground" />
      <span className="truncate">{a.filename}</span>
      <span className="shrink-0 text-muted-foreground">{formatBytes(a.size)}</span>
    </a>
  );
}

/**
 * An image preview; if it can't load (an expired link, say) it falls back to a file link. `opens`: the
 * file this image previews, upstream (T-99), which a click (or the fallback link) opens instead of the image.
 */
function ImagePreview({ a, opens, where }: { a: TaskAttachment; opens?: string; where?: string }) {
  const [failed, setFailed] = useState(false);
  const link = useAttachmentLink(a);
  if (failed) return <FileLink a={a} opens={opens} />;
  return (
    <a {...(opens ? { href: opens, target: '_blank', rel: 'noreferrer' } : link)} data-hint={opens ? `Open on ${where ?? 'the source'}` : a.filename}>
      <img
        src={a.url}
        alt={a.filename}
        className="max-h-40 max-w-full rounded-md border object-contain sm:max-w-[16rem]"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </a>
  );
}

/** An MP4 that plays in place; like an image, it falls back to a file link if it can't load. */
function VideoPreview({ a }: { a: TaskAttachment }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <FileLink a={a} />;
  return (
    <video
      src={a.url}
      controls
      playsInline
      preload="metadata"
      title={a.filename}
      className="block max-h-72 w-full max-w-md rounded-md border bg-black"
      onError={() => setFailed(true)}
    />
  );
}

/** `opens` (T-99): thumbnails of files upstream, by attachment id, with where each file opens; `where` names the source. */
export function AttachmentList({ attachments, opens = {}, where }: { attachments: TaskAttachment[]; opens?: Record<string, string>; where?: string }) {
  if (!attachments.length) return null;
  const images = attachments.filter((a) => a.is_image);
  const videos = attachments.filter((a) => isPlayableVideo(a));
  const files = attachments.filter((a) => !a.is_image && !isPlayableVideo(a));
  return (
    <div className="mt-2 space-y-2">
      {images.length ? (
        <div className="flex flex-wrap gap-2">
          {images.map((a) => (
            <ImagePreview key={a.id} a={a} opens={opens[a.id]} where={where} />
          ))}
        </div>
      ) : null}
      {videos.map((a) => (
        <VideoPreview key={a.id} a={a} />
      ))}
      {files.map((a) => (
        <FileLink key={a.id} a={a} />
      ))}
    </div>
  );
}

function ChatMessage({
  task,
  event,
  members,
  runLink,
  onNavigate,
  answered = false,
  closed = false,
  steersSent,
  projectSlug,
  commentSend,
}: {
  task: TaskDetail;
  event: TaskEvent;
  members: TaskMember[];
  runLink?: RunLink;
  onNavigate?: () => void;
  /** Its questions (T-10) are answered: a person has written since, or (T-58) they were sent upstream and answered. */
  answered?: boolean;
  /** SF-T-279: its questions closed unanswered (a person closed the run). */
  closed?: boolean;
  /** On a progress report with steering options (T-44): the options people have sent since. */
  steersSent?: string[];
  /** T-41: the project's slug, so a comment's "Sent to R-N" tag links to that run. */
  projectSlug?: string;
  /** SF-T-201: Send to <source> in its ⋯ menu, when it can go. */
  commentSend?: CommentSend;
}) {
  const me = useAuthStore((s) => s.user?.id);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState<{ text: string; mentions: PickedMention[] } | null>(null);
  // T-70: a long comment shows its AI line (or its first lines until that's made), with More for the rest.
  const [full, setFull] = useState(false);
  const long = isLongComment(event.body);
  const pending = usePendingEvent(task.id, event.id);
  const author = members.find((m) => m.user_id === event.actor_user_id);
  // SF-T-74: a settled review's decisions stay as written (the worker refuses edits and deletes).
  const mine = event.actor_user_id === me && !pending && !parseDisputeDecisions(event.body ?? '');
  // T-58: the message that went upstream stays as sent (replies are matched against it): no Edit or Delete.
  const ownActions = mine && !deferredMarker(event.payload);
  const sendable = Boolean(commentSend) && !pending && canSendComment(event);
  const steeredRun = steeredRunNumber(event);
  const heldRun = heldRunNumber(event);

  const editTooLong = editing ? commentTooLong(displayToMarkup(editing.text, editing.mentions)) : null;
  // An edit that differs from the message asks before it's lost (leaving, or the Inbox's Next, SF-T-236).
  useUnsavedChangesGuard(editing ? displayToMarkup(editing.text, editing.mentions) !== (event.body ?? '') : false, 'Your edit to this message isn’t saved. Leave anyway?');
  const save = () => {
    if (!editing || editTooLong) return;
    const body = displayToMarkup(editing.text, editing.mentions);
    setEditing(null);
    const editedAt = new Date().toISOString();
    void optimisticUpdate(
      task.id,
      { detail: (t) => ({ ...t, events: t.events.map((e) => (e.id === event.id ? { ...e, body, edited_at: editedAt, ai_short: null } : e)) }) },
      editTaskChat(task.id, event.id, body)
    );
  };

  const remove = async () => {
    const ok = await confirmDelete({ what: 'message', name: 'this message', consequence: 'Its files are deleted too.' });
    if (!ok) return;
    void optimisticUpdate(
      task.id,
      {
        // Deleted messages leave the timeline altogether, and the card's counts.
        detail: (t) => ({ ...t, events: t.events.filter((e) => e.id !== event.id) }),
        counts: { chat: -1, attachments: -event.attachments.length },
      },
      deleteTaskChat(task.id, event.id)
    );
  };

  // SF-T-160: links in comments open in dialogs, so a thread's links can be walked through in place.
  const renderText = (text: string, className: string) => (
    <LinksOpenInDialogs>
      <MentionText
        text={text}
        members={members}
        contexts={task.contexts}
        onNavigate={onNavigate}
        markdown
        // SF-T-76: a comment copied in from the task's source names its people and tasks the source's way.
        sourceRefs={isSyncedIn(event) ? task.source_refs : null}
        // The factory's messages can carry the AI builder's questions (T-10): they're answered and kept
        // in the task's Decisions under the description (T-22), so here they're one line pointing there.
        questions={event.actor_label ? { answered, closed, onShow: () => showTaskDecisions(task.id, event.id) } : undefined}
        // Its suggestions (T-14) are decided in the task's Suggestions, also under the description.
        onShowSuggestions={event.actor_label ? () => showTaskSuggestions(task.id, event.id) : undefined}
        // Its proposed sub-tasks are added at the top of the task, then kept in its Proposed sub-tasks (SF-T-178).
        onShowSubtasks={event.actor_label ? () => showProposedSubtasks(task.id, event.id) : undefined}
        // A disputed review's findings (SF-T-74) are settled in the task's Decisions too.
        onShowDecisions={event.actor_label ? () => showTaskDecisions(task.id, event.id) : undefined}
        // A progress report's steering options (T-44) send while its run is going.
        steer={
          steersSent
            ? {
                active: Boolean(runLink?.run && isRunActive(runLink.run.status)),
                sent: steersSent,
                onSteer: (option) => sendMessage(task.id, steerComment(option), []),
              }
            : undefined
        }
        className={className}
      />
    </LinksOpenInDialogs>
  );

  return (
    <div className="group relative flex gap-3">
      <MemberAvatar
        name={event.actor_label ?? memberName(author)}
        photourl={event.actor_label ? null : author?.photourl}
        size="sm"
        ring
        className="z-10 mt-1"
      />
      <div
        className={cn(
          'min-w-0 flex-1 rounded-lg border bg-card px-3 py-2 shadow-xs transition-opacity',
          pending?.status === 'sending' && 'opacity-70',
          pending?.status === 'failed' && 'border-destructive/50'
        )}
      >
        <div className="flex items-center gap-2 text-xs">
          <ActorName label={event.actor_label} member={author} className="font-medium text-foreground" runLink={runLink} onNavigate={onNavigate} synced={isSyncedIn(event)} />
          <ViaApiKey name={event.via_api_key_name} />
          <span className="text-muted-foreground" data-hint={formatDateTime(new Date(event.created_at))}>
            {pending?.status === 'sending' ? (
              <span className="inline-flex items-center gap-1">
                <Spinner size="xs" />
                {event.attachments.length ? 'Uploading…' : 'Sending…'}
              </span>
            ) : (
              formatRelative(event.created_at)
            )}
            {event.edited_at ? ' · edited' : ''}
            {upstreamNote(event, task.upstream)}
          </span>
          {/* T-41: this comment went into the run's working agent, or waits for it to work. */}
          {steeredRun !== null ? <SteeredTag runNumber={steeredRun} projectSlug={projectSlug} onNavigate={onNavigate} /> : null}
          {heldRun !== null ? <SteeredTag runNumber={heldRun} waiting projectSlug={projectSlug} onNavigate={onNavigate} /> : null}
          {pending?.status === 'failed' ? (
            <span className="flex items-center gap-2 font-medium text-destructive">
              Couldn’t send
              <button type="button" className="underline" onClick={pending.retry}>
                Retry
              </button>
              <button type="button" className="text-muted-foreground underline" onClick={() => dismissEvent(task.id, event.id)}>
                Discard
              </button>
            </span>
          ) : null}
          {!editing && (ownActions || sendable) ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-xs" className="-my-1 ml-auto opacity-0 group-hover:opacity-100 focus-visible:opacity-100" aria-label="Message actions">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {sendable && commentSend ? <DropdownMenuItem onSelect={() => setSending(true)}>Send to {commentSend.sourceLabel}…</DropdownMenuItem> : null}
                {ownActions && event.body ? (
                  <DropdownMenuItem onSelect={() => setEditing(markupToDisplay(event.body ?? ''))}>Edit</DropdownMenuItem>
                ) : null}
                {ownActions ? (
                  <DropdownMenuItem variant="destructive" onSelect={() => void remove()}>
                    Delete
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
        {editing ? (
          <div className="mt-1 space-y-2">
            <MentionTextarea
              aria-label="Edit message"
              tagging
              aiMachines
              value={editing.text}
              onChange={(text) => setEditing((e) => (e ? { ...e, text } : e))}
              mentions={editing.mentions}
              onMentionsChange={(mentions) => setEditing((e) => (e ? { ...e, mentions } : e))}
              members={members}
              rows={2}
              autoFocus
              onSubmit={save}
            />
            {editTooLong ? <p className="text-xs text-destructive">{editTooLong}</p> : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button size="sm" disabled={!editing.text.trim() || Boolean(editTooLong)} onClick={save}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            {event.body ? (
              long && !full ? (
                <ShortComment
                  short={event.ai_short ?? null}
                  parts={splitCommentParts(event.body)}
                  renderText={renderText}
                  onMore={() => setFull(true)}
                />
              ) : (
                <>
                  {renderText(event.body, 'mt-0.5 text-sm leading-relaxed')}
                  {long ? <MoreLess label="Less" onClick={() => setFull(false)} /> : null}
                </>
              )
            ) : null}
            <AttachmentList attachments={event.attachments} opens={upstreamOpens(event)} where={task.upstream ? TASK_SOURCE_LABELS[task.upstream.kind] : undefined} />
          </>
        )}
      </div>
      {commentSend ? (
        <SendCommentUpstreamDialog
          open={sending}
          onOpenChange={setSending}
          sourceLabel={commentSend.sourceLabel}
          author={event.actor_label ? 'AI Agent' : memberName(author)}
          mine={!event.actor_label && event.actor_user_id === me}
          files={event.attachments.length}
          tagging={commentSend.tagging}
          onSend={(mentionId) => commentSend.onSend(event.id, mentionId)}
        />
      ) : null}
    </div>
  );
}

/** More or Less under a long comment. */
function MoreLess({ label, onClick }: { label: 'More' | 'Less'; onClick: () => void }) {
  return (
    <Button
      variant="link"
      size="xs"
      // Tasks is a phone page: a 44px tap target there (forms-and-inputs.project.mdc, "Phone pages").
      className="-ml-2 h-6 text-muted-foreground hover:text-foreground max-md:h-11"
      aria-expanded={label === 'Less'}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

/**
 * T-70: a long comment, shortened: its AI line, or its first lines faded out until the line is made,
 * then its factory blocks (questions, suggestions, steering options: already one line or a card each)
 * and More for the whole comment.
 */
function ShortComment({
  short,
  parts,
  renderText,
  onMore,
}: {
  short: string | null;
  parts: { prose: string; factoryBlocks: string };
  renderText: (text: string, className: string) => ReactNode;
  onMore: () => void;
}) {
  return (
    <>
      {short ? (
        <p className="mt-0.5 text-sm leading-relaxed">
          <Sparkles className="mr-1 inline size-3.5 align-[-2px] text-ai" aria-label="AI summary of this comment" />
          {short}
        </p>
      ) : (
        <div className="max-h-28 overflow-hidden [mask-image:linear-gradient(to_bottom,black_55%,transparent)]">
          {renderText(parts.prose, 'mt-0.5 text-sm leading-relaxed')}
        </div>
      )}
      {parts.factoryBlocks ? renderText(parts.factoryBlocks, 'mt-1 text-sm leading-relaxed') : null}
      <MoreLess label="More" onClick={onMore} />
    </>
  );
}

const KIND_ICONS: Record<TaskEvent['kind'], LucideIcon> = {
  created: Plus,
  chat: MessageSquare,
  moved: ArrowRight,
  renamed: Pencil,
  described: Pencil,
  spec_changed: Pencil,
  assigned: UserPlus,
  unassigned: UserMinus,
  context_added: Link2,
  context_removed: Unlink,
  label_added: Tag,
  label_removed: Tag,
  archived: Archive,
  unarchived: ArchiveRestore,
  promoted: CircleCheck,
  pr_batch_merged: GitMerge,
  snoozed: AlarmClock,
  unsnoozed: AlarmClockOff,
  repeat_set: Repeat,
  repeated: Repeat,
  type_set: Shapes,
  field_set: SlidersHorizontal,
  waiting_added: Hourglass,
  waiting_removed: CircleCheck,
  orphaned: ListTree,
  subtask_promoted: ArrowUpFromLine,
  made_subtask: ListTree,
  attachment_deleted: Trash2,
  merged: ListTree,
  pushed_upstream: ArrowUpFromLine,
  runs_closed: CircleSlash,
};

/** The task's runs, for linking "Factory · R-3" to its run page (TaskDetailView loads them, `useTaskRuns`). */
export interface TimelineRuns {
  projectSlug: string;
  /** The loaded runs by number; a run not in it yet still links, without a status. */
  byNumber: Map<number, RunSummary>;
}

/** An event made by a run: where it links, and whether this line carries the run's status. */
interface RunLink {
  projectSlug: string;
  number: number;
  run: RunSummary | undefined;
  /** Only each run's newest line shows its status, so a long run doesn't repeat it on every line. */
  showStatus: boolean;
}

/** For each run named in `events`, the id of its newest event there. */
function newestEventPerRun(events: TaskEvent[]): Map<number, string> {
  const newest = new Map<number, string>();
  for (const event of events) {
    const number = runNumberFromActorLabel(event.actor_label);
    if (number !== null) newest.set(number, event.id);
  }
  return newest;
}

/** Who did it: the person, or the factory's run ("Factory · R-3 · dev-box", started by that person, on that machine) when the event has a label. */
function ActorName({
  label,
  member,
  className,
  runLink,
  onNavigate,
  synced = false,
}: {
  label: string | null;
  member: Parameters<typeof memberName>[0];
  className: string;
  runLink?: RunLink;
  onNavigate?: () => void;
  /** T-15: the line came from the task's source (isSyncedIn). */
  synced?: boolean;
}) {
  if (label && runLink) {
    const status = runLink.showStatus && runLink.run ? runLink.run.status : null;
    return (
      <span className="inline-flex items-center gap-1.5 align-middle">
        <Link
          to={runPath(runLink.projectSlug, runLink.number)}
          onClick={onNavigate}
          className={cn('inline-flex items-center gap-1 hover:underline', className)}
          data-hint={`Open ${runRef(runLink.number)}, started by ${memberName(member)}`}
        >
          <Bot className="size-3" />
          {label}
        </Link>
        {/* The fleet machine doing the work (T-29); unknown until the run's picked up. */}
        {runLink.run?.machine ? (
          <span className="font-normal text-muted-foreground" data-hint={`${runRef(runLink.number)} runs on ${runLink.run.machine.name}`}>
            · {runLink.run.machine.name}
          </span>
        ) : null}
        {status ? <StatusBadge meta={RUN_STATUS_META[status]} size="sm" /> : null}
      </span>
    );
  }
  // Synced in from the task's source (T-15): a person upstream, not the factory.
  if (label && synced) {
    return (
      <span className={cn('inline-flex items-center gap-1', className)} data-hint="Written upstream and synced in">
        <ArrowDownToLine className="size-3" />
        {label}
      </span>
    );
  }
  if (label) {
    return (
      <span className={cn('inline-flex items-center gap-1', className)} data-hint={`Started by ${memberName(member)}`}>
        <Bot className="size-3" />
        {label}
      </span>
    );
  }
  return (
    <span className={className} data-hint={memberName(member)}>
      {memberHandle(member)}
    </span>
  );
}

/** "via Claude key": a member's API key made this (Settings › API keys). */
function ViaApiKey({ name }: { name: string | null }) {
  if (!name) return null;
  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground" data-hint="Made with this API key">
      <KeyRound className="size-3" />
      via {name}
    </span>
  );
}

function SystemLine({
  task,
  event,
  members,
  labels,
  runLink,
  onNavigate,
}: {
  task: TaskDetail;
  event: TaskEvent;
  members: TaskMember[];
  labels: TaskLabel[];
  runLink?: RunLink;
  onNavigate?: () => void;
}) {
  const Icon = KIND_ICONS[event.kind] ?? Pencil;
  const actor = members.find((m) => m.user_id === event.actor_user_id);
  const formatRef = useFormatTaskRef();
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      <span className="z-10 flex size-6 shrink-0 items-center justify-center rounded-full border bg-background">
        <Icon className="size-3" />
      </span>
      <span className="min-w-0 flex-1">
        {/* A snooze ending by itself or a Repeat coming round has no one to name; Jev's suggestions name Jev (actor_label). */}
        {!hasNoActor(event) ? (
          <>
            <ActorName
              label={event.actor_label}
              member={actor}
              className="font-medium text-foreground/80"
              runLink={runLink}
              onNavigate={onNavigate}
              synced={isSyncedIn(event)}
            />{' '}
          </>
        ) : null}
        {event.via_api_key_name ? (
          <>
            <ViaApiKey name={event.via_api_key_name} />{' '}
          </>
        ) : null}
        {systemLine(event, members, labels, formatRef)}
        {(event.kind === 'described' || event.kind === 'spec_changed') && event.payload.has_before ? (
          <>
            {' · '}
            <DescriptionBefore
              task={task}
              event={event}
              members={members}
              onNavigate={onNavigate}
              field={event.kind === 'spec_changed' ? 'spec' : 'description'}
            />
          </>
        ) : null}
      </span>
      <span className="shrink-0" data-hint={formatDateTime(new Date(event.created_at))}>
        {formatRelative(event.created_at)}
      </span>
    </div>
  );
}


/** Runs of system lines longer than this fold down to the last two, with a "show" button. */
const FOLD_AFTER = 3;

type Block = { kind: 'chat'; event: TaskEvent } | { kind: 'updates'; key: string; events: TaskEvent[] };

function toBlocks(events: TaskEvent[]): Block[] {
  const blocks: Block[] = [];
  for (const event of events) {
    const last = blocks[blocks.length - 1];
    if (event.kind === 'chat') blocks.push({ kind: 'chat', event });
    else if (last?.kind === 'updates') last.events.push(event);
    else blocks.push({ kind: 'updates', key: event.id, events: [event] });
  }
  return blocks;
}

/**
 * "Show changes": the timeline's system lines on or off, remembered per browser for every task (on unless
 * turned off). In the timeline's own header, or (SF-T-305) on the pop-up's top row beside its tabs.
 */
export function ShowChangesToggle({ className }: { className?: string }) {
  const [showActivity, setShowActivity] = usePref(UI_PREFS.tasksShowChanges);
  return (
    // In a narrow activity pane (dragged under ~20rem) the words go, so the switch keeps clear of the pop-up's close button.
    <label className={cn('flex items-center gap-2 whitespace-nowrap text-xs text-muted-foreground', className)} data-hint="Show changes">
      <Switch checked={showActivity} onCheckedChange={setShowActivity} aria-label="Show changes" />
      <span className="@max-[20rem]/activity:sr-only">Show changes</span>
    </label>
  );
}

/** Chat and history in one stream, newest at the bottom, with the composer under it. */
export function TaskTimeline({
  task,
  hydrated = true,
  showComposer = true,
  fill = false,
  header: showHeader = true,
  members,
  labels,
  runs,
  onNavigate,
  commentSend,
}: {
  task: TaskDetail;
  /** False while only the card is known: the timeline shows a placeholder. */
  hydrated?: boolean;
  /** Phones pin the chat box to the bottom of the screen instead (see TaskDetailView). */
  showComposer?: boolean;
  /** Fills its pane (the pop-up's right side): the stream scrolls, the chat box stays at the foot. */
  fill?: boolean;
  /** The Activity heading and Show changes; false where the pop-up shows the toggle on its top row (SF-T-305). */
  header?: boolean;
  members: TaskMember[];
  labels: TaskLabel[];
  /** The task's runs: "Factory · R-3" links to its run, and its newest line shows the run's status. */
  runs?: TimelineRuns;
  onNavigate?: () => void;
  /** SF-T-201: Send to <source> on comments, on a mirrored task you can edit. */
  commentSend?: CommentSend;
}) {
  const [showActivity] = usePref(UI_PREFS.tasksShowChanges);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const events = showActivity ? task.events : task.events.filter((e) => e.kind === 'chat');
  const allBlocks = toBlocks(events);
  // Collapsed each time a task opens. The first comment shown is fixed when its activity loads, so new
  // messages (and a comment being edited, data-freshness.mdc) never move it; null shows everything.
  const [anchor, setAnchor] = useState<{ taskId: string; eventId: string | null } | null>(null);
  const phone = useIsMobile();
  if (hydrated && anchor?.taskId !== task.id) {
    const at = collapsedStart(allBlocks, phone ? RECENT_COMMENTS_PHONE : RECENT_COMMENTS);
    const first = at > 0 ? allBlocks[at] : null;
    setAnchor({ taskId: task.id, eventId: first?.kind === 'chat' ? first.event.id : null });
  }
  const anchorId = anchor?.taskId === task.id ? anchor.eventId : null;
  const start = anchorId ? Math.max(0, allBlocks.findIndex((b) => b.kind === 'chat' && b.event.id === anchorId)) : 0;
  const blocks = allBlocks.slice(start);
  const earlierComments = allBlocks.slice(0, start).filter((b) => b.kind === 'chat').length;
  const newestPerRun = newestEventPerRun(events);
  // The factory's messages whose questions are answered: by the rule the Decisions use (T-58: questions
  // sent upstream stay open until they're answered, whatever else people write).
  const rounds = useMemo(() => decisionRounds(task.events), [task.events]);
  const answeredRounds = useMemo(() => new Set(rounds.filter((r) => r.answered).map((r) => r.event.id)), [rounds]);
  // SF-T-279: closed unanswered, as a person closed the run that asked.
  const closedRounds = useMemo(() => new Set(rounds.filter((r) => r.closed).map((r) => r.event.id)), [rounds]);
  const runLinkFor = (event: TaskEvent): RunLink | undefined => {
    const number = runNumberFromActorLabel(event.actor_label);
    if (!runs || number === null) return undefined;
    return { projectSlug: runs.projectSlug, number, run: runs.byNumber.get(number), showStatus: newestPerRun.get(number) === event.id };
  };

  // Filling a pane, it opens on the newest message and follows new ones, until you scroll up to read (SF-T-222:
  // then Jump to latest, or scrolling back to the bottom, follows again).
  const stream = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const { following, follow } = useFollowScroll(stream, content, fill, task.id);

  const header = (
      <div className="flex shrink-0 items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Activity</h3>
        <ShowChangesToggle />
      </div>
  );

  const body = (
    <>
      {!hydrated && !blocks.length ? (
        <div className="space-y-3" aria-label="Loading activity">
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="size-6 animate-pulse rounded-full bg-muted" />
              <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : blocks.length ? (
        <div className="relative space-y-3 before:absolute before:bottom-2 before:left-3 before:top-2 before:w-px before:bg-border">
          {start > 0 ? (
            <button
              type="button"
              className="relative z-10 ml-9 text-xs font-medium text-muted-foreground hover:text-foreground max-md:min-h-11"
              aria-expanded={false}
              onClick={() => setAnchor({ taskId: task.id, eventId: null })}
            >
              Show {earlierComments} earlier {earlierComments === 1 ? 'comment' : 'comments'}
            </button>
          ) : null}
          {blocks.map((block) => {
            if (block.kind === 'chat') {
              return (
                <ChatMessage
                  key={block.event.id}
                  task={task}
                  event={block.event}
                  members={members}
                  runLink={runLinkFor(block.event)}
                  onNavigate={onNavigate}
                  answered={answeredRounds.has(block.event.id)}
                  closed={closedRounds.has(block.event.id)}
                  steersSent={hasSteerOptions(block.event) ? steersSentAfter(task.events, task.events.indexOf(block.event)) : undefined}
                  projectSlug={runs?.projectSlug}
                  commentSend={commentSend}
                />
              );
            }
            const folded = block.events.length > FOLD_AFTER && !expanded.has(block.key);
            const shown = folded ? block.events.slice(-2) : block.events;
            return (
              <div key={block.key} className="space-y-2">
                {folded ? (
                  <button
                    type="button"
                    className="relative z-10 ml-9 text-xs font-medium text-muted-foreground hover:text-foreground"
                    onClick={() => setExpanded((prev) => new Set(prev).add(block.key))}
                  >
                    Show {block.events.length - shown.length} earlier changes
                  </button>
                ) : null}
                {shown.map((event) => (
                  <SystemLine
                    key={event.id}
                    task={task}
                    event={event}
                    members={members}
                    labels={labels}
                    runLink={runLinkFor(event)}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState size="compact" title="No messages yet" />
      )}
    </>
  );

  return (
    <section className={cn(fill ? 'flex h-full min-h-0 flex-col gap-3' : 'space-y-3')}>
      {showHeader ? header : null}
      {fill ? (
        <div className="relative -mx-1 min-h-0 flex-1">
          <div ref={stream} className="h-full overflow-y-auto px-1 py-1">
            <div ref={content}>{body}</div>
          </div>
          {following ? null : <JumpToLatestButton className="bottom-2" onClick={follow} />}
        </div>
      ) : (
        body
      )}
      {showComposer ? (
        <div className={cn(fill && 'shrink-0')}>
          <TaskChatComposer taskId={task.id} members={members} upstream={task.upstream} />
        </div>
      ) : null}
    </section>
  );
}
